import { FUNNEL_STAGE, FunnelStage } from 'lib/activationJourney/constants'

// Google Analytics 4 hard limits. GA4 does not error on a violation — it
// silently drops the event or the offending parameter — so every one of these
// is enforced client-side before an event leaves the browser.
// Ref: https://support.google.com/analytics/answer/9267744
export const GA_EVENT_NAME_MAX_LENGTH = 40
export const GA_PARAM_NAME_MAX_LENGTH = 40
export const GA_PARAM_VALUE_MAX_LENGTH = 100
export const GA_MAX_PARAMS_PER_EVENT = 25

// gtag command verbs.
export const GA_COMMAND_JS = 'js'
export const GA_COMMAND_CONFIG = 'config'
export const GA_COMMAND_SET = 'set'
export const GA_COMMAND_EVENT = 'event'

export const GA_PAGE_VIEW_EVENT = 'page_view'
export const GA_USER_ID_KEY = 'user_id'
export const GA_SCRIPT_ID = 'ga-gtag-script'

// Personally identifying fields that ride on every trackEvent payload through
// window.globals._logData. Sending these to GA breaks Google's measurement
// policy (grounds for the property being terminated), so they are stripped from
// the GA copy only — PostHog and the GTM dataLayer push keep the full payload.
export const GA_PII_KEYS = ['email', 'name', 'phone', 'customer_id']

// trackEvent names longer than GA_EVENT_NAME_MAX_LENGTH are truncated to fit.
// These are the ones whose first 40 characters are NOT unique, so truncation
// alone would merge distinct events into a single GA name. Each override is a
// hand-shortened, still-readable equivalent. The googleAnalytics test asserts
// that every event in trackEvent.events maps to a unique name within the limit,
// so a future long event that collides fails the suite instead of silently
// merging in the GA reports.
export const GA_EVENT_NAME_OVERRIDES: Record<string, string> = {
  onboarding_shopify_embedded_chat_button_save:
    'onboarding_shopify_emb_chat_btn_save',
  onboarding_shopify_embedded_chat_button_discard:
    'onboarding_shopify_emb_chat_btn_discard',
  onboarding_shopify_embedded_handoff_retry:
    'onboarding_shopify_emb_handoff_retry',
  onboarding_shopify_embedded_open_zoko_click:
    'onboarding_shopify_emb_open_zoko_click',
  onboarding_shopify_embedded_checklist_step:
    'onboarding_shopify_emb_checklist_step',
  onboarding_shopify_embedded_checklist_dismiss:
    'onboarding_shopify_emb_checklist_dismiss',
  billing_shopify_migration_credits_migrate_success:
    'billing_shopify_migr_credits_success',
  billing_shopify_migration_credits_migrate_failure:
    'billing_shopify_migr_credits_failure',
  onboarding_mqo_business_phone_duplicate_error:
    'onboarding_mqo_biz_phone_dup_error',
  onboarding_mqo_business_phone_duplicate_error_close_click:
    'onboarding_mqo_biz_phone_dup_close',
  onboarding_mqo_business_phone_duplicate_error_contact_us_click:
    'onboarding_mqo_biz_phone_dup_contact_us',
  onboarding_mqo_business_phone_duplicate_error_go_back_to_login_click:
    'onboarding_mqo_biz_phone_dup_to_login',
  onboarding_shopify_welcome_unable_to_scan_click:
    'onboarding_shopify_welcome_scan_click',
  onboarding_shopify_welcome_unable_to_scan_submitted:
    'onboarding_shopify_welcome_scan_submit',
}

// Prefix for the demo-store mirror events (demo_<eventName>) emitted alongside
// the real event while the active store is a demo store.
export const DEMO_EVENT_PREFIX = 'demo_'

// Maps the events that already fire at each activation-funnel milestone to the
// stage they represent. trackEvent consults this map itself, so the firing
// sites need no changes and a new stage is a single line here.
//
// Two entries are deliberately not the obvious ones, both verified against
// production data (Aug 2026):
//  - The journey is anchored on auth_create_account_visit, NOT auth_signup_lead.
//    The lead event fires a median ~19 minutes in, after the demo store already
//    exists, so anchoring on it would start the clock mid-funnel and make the
//    signup stage unreachable (a backwards move, which markStage ignores).
//  - Meta onboarding accepts both the live onboarding_connect_account_visit and
//    the retired onboarding_meta_connect_visit, which stopped firing 2026-08-03.
//    Mapping two events to one stage is safe: markStage is forward-only, so
//    whichever arrives first wins and the second is a no-op.
export const FUNNEL_STAGE_EVENTS: Record<string, FunnelStage> = {
  auth_create_account_visit: FUNNEL_STAGE.signup,
  onboarding_demo_store_create_success: FUNNEL_STAGE.demo_store,
  onboarding_connect_account_visit: FUNNEL_STAGE.meta_onboarding,
  onboarding_meta_connect_visit: FUNNEL_STAGE.meta_onboarding,
  onboarding_qr_shown: FUNNEL_STAGE.qr_shown,
  onboarding_qr_code_accepted: FUNNEL_STAGE.qr_accepted,
}

// PostHog group type keyed on the Shopify permanent domain (`<x>.myshopify.com`).
// PostHog assigns the group's numeric index itself when the type is first seen,
// and that index shifts as group types are added or removed — so it is not
// recorded here. Look it up before writing SQL against `$group_N`.
//
// The existing `store` group cannot key the Shopify signup funnel: the Zoko
// store does not exist until midway through it, so the first steps have no
// store_id. The shop domain is present from the very first event of an install,
// which makes it the only identifier that spans the whole funnel.
//
// It has to be a GROUP rather than a plain property because a PostHog funnel
// can only aggregate by person, session or group — and person does not work
// here, since billing_update is emitted server-side under a different person to
// the one who onboarded.
export const POSTHOG_GROUP_SHOPIFY = 'shopify'
// PostHog skips autocapture for any element (or descendant) with this class.
export const POSTHOG_NO_CAPTURE_CLASS = 'ph-no-capture'

// Entry property holding that domain. trackEvent merges it onto every event of
// the session from the journeySink entry, so it is read off the payload rather
// than passed per call site.
export const SHOP_PROPERTY = 'shop'

// The same domain as the backend knows it, carried on _logData once a store
// exists. Used as a fallback source for the group: the journeySink entry lives
// in sessionStorage, so it is gone in a new tab, a later session or on another
// device, whereas this rides on the store itself. Measured across a real
// install journey, one or the other is present on 46 of 47 create-account
// visits — and the group only has to be registered once per browser, since
// PostHog then persists it.
export const STORE_DOMAIN_PROPERTY = 'store_domain'
